import { TEmployeeRenewVisaDocumentStepGroup } from "@/api/entities/employee-renew-visa-document-step-group.entity";
import { compressPdfApi, uploadFileApi } from "@/api/services/global/file";
import {
  fetchRenewVisaDocumentCreationGroupDownloadListApi,
  finishRenewVisaDocumentCreationGroupApi,
  updateRenewVisaDocumentMergedPdfFilePathApi,
  useGetRenewVisaDocumentGroupInfo,
} from "@/api/services/main/renew-visa";
import { TGetRenewVisaDocumentCreationGroupOutputDto } from "@/api/services/main/renew-visa/dtos/get-renew-visa-document-creation-group-list.dto";
import { Button } from "@/components/elements/Button";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { EXTENSIONS, FILE_UPLOAD_FOR } from "@/constants/file";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useConvertRenewVisaToCsvData } from "@/features/renew-visa/hook/useConvertRenewVisaToCsvData";
import { useMergeRenewVisaFile } from "@/features/renew-visa/hook/useMergeRenewVisaFile";
import { RENEW_VISA_DOCUMENT_STEP_STATUS } from "@/types/enum";
import { showError } from "@/utils/error";
import { generateFileFromFilePath } from "@/utils/export";
import { toNumber } from "@/utils/number";
import { getFullName } from "@/utils/string";
import { toast } from "@/utils/toast";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import iconv from "iconv-lite";
import { json2csv } from "json-2-csv";
import JSZip from "jszip";
import { FC, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

type TDownloadDocumentCreationFileZipBtnProps = {
  stepGroupData: TEmployeeRenewVisaDocumentStepGroup;
  renewVisaDocumentCreationGroupList: TGetRenewVisaDocumentCreationGroupOutputDto[] | undefined;
  readonly: boolean;
};

export const DownloadDocumentCreationFileZipBtn: FC<TDownloadDocumentCreationFileZipBtnProps> = ({
  renewVisaDocumentCreationGroupList,
  stepGroupData,
  readonly,
}) => {
  const queryClient = useQueryClient();

  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const { employeeRenewVisaDocumentGroupId } = useParams();

  const { convertRenewVisaToCsvData } = useConvertRenewVisaToCsvData();

  const { data: renewVisaDocumentGroup } = useGetRenewVisaDocumentGroupInfo();

  const { generateMergedPdf } = useMergeRenewVisaFile();

  const getUploadAvatarFile = async (avatarUrl: string) => {
    const res = await axios.get(avatarUrl, { responseType: "blob" });
    const extensionName = res.data.type.split("/").pop() as string;
    const extension = EXTENSIONS.IMAGE.includes(`.${extensionName?.toLowerCase()}`) ? extensionName : "jpeg";
    const avatarBlob = res.data;
    return { avatarBlob, extension };
  };

  const isCompleteStep = useMemo(
    () =>
      renewVisaDocumentCreationGroupList?.every(
        (item) =>
          item.renewVisaDocumentStep.renewVisaCompanyApplicationDocument?.status === RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE &&
          item.renewVisaDocumentStep.renewVisaEmployeeApplicationDocument?.status === RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE &&
          item.renewVisaDocumentStep.renewVisaUploadAvatarFile?.status === RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE &&
          item.renewVisaDocumentStep.renewVisaUploadFile?.status === RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE,
      ),
    [renewVisaDocumentCreationGroupList],
  );

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      if (!renewVisaDocumentCreationGroupList || !renewVisaDocumentGroup || !employeeRenewVisaDocumentGroupId) return;

      if (stepGroupData.status !== RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE) {
        await finishRenewVisaDocumentCreationGroupApi(employeeRenewVisaDocumentGroupId, stepGroupData?.id);
      }

      const renewVisaDocumentCreationGroupDownloadList = await fetchRenewVisaDocumentCreationGroupDownloadListApi(
        employeeRenewVisaDocumentGroupId,
        toNumber(stepGroupData?.id),
      );

      if (!renewVisaDocumentCreationGroupDownloadList) return;

      const zip = new JSZip();

      //handle csv
      const renewVisaList = renewVisaDocumentCreationGroupDownloadList.data
        ? renewVisaDocumentCreationGroupDownloadList.data.map((item) => ({
            employeeApplication: item.renewVisaDocumentStep.renewVisaEmployeeApplicationDocument,
            companyApplication: item.renewVisaDocumentStep.renewVisaCompanyApplicationDocument,
          }))
        : [];

      if (renewVisaList.length > 0) {
        const csvDataArrayJson = convertRenewVisaToCsvData(renewVisaList);

        const csvData = json2csv(csvDataArrayJson, {
          prependHeader: true,
          delimiter: { field: "\t", eol: "\r\n" },
          expandArrayObjects: true,
          escapeHeaderNestedDots: false,
        });

        const lines = csvData.split("\r\n");

        if (lines.length > 0) {
          const headers = lines[0].split("\t");

          // clear header
          headers[0] = "";
          headers[1] = "";
          headers[2] = "";
          headers[62] = "";
          headers[331] = "";
          headers[332] = "";
          headers[335] = "";
          headers[336] = "";

          lines[0] = headers.join("\t");
        }

        const fixedCsv = lines.join("\r\n");

        // Encode the CSV content to UTF-16LE
        const csvWithBom = "\uFEFF" + fixedCsv + "\r\n";
        const utf16Csv = iconv.encode(csvWithBom, "utf16le");
        const blob = new Blob([utf16Csv as BlobPart], { type: "text/csv;charset=utf-16le;" });
        zip.file("一括申請データ_ver001.csv", blob);
      }

      await Promise.all(
        renewVisaDocumentCreationGroupDownloadList.data.map(async (item) => {
          const { firstName, lastName, middleName } = item.renewVisaDocumentStep.renewVisaEmployeeApplicationDocument.employee;
          const fullName = getFullName({ firstName, lastName, middleName });

          const employeeFolder = zip.folder(fullName);
          // Handle merged file pdf
          let mergedPdf: File | null | undefined = null;
          if (item.renewVisaDocument.mergedPdfFilePath) {
            mergedPdf = await generateFileFromFilePath(item.renewVisaDocument.mergedPdfFilePath);
          } else {
            mergedPdf = await generateMergedPdf(
              item.renewVisaDocumentStep.renewVisaEmployeeApplicationDocument,
              item.renewVisaDocumentStep.renewVisaUploadFile,
            );
            if (!!mergedPdf) {
              if (mergedPdf.size > 9.8 * 1000 * 1000) {
                const compressRes = await compressPdfApi({ targetSizeMb: 9.8, file: mergedPdf }).then((res) => res.data);
                mergedPdf = await generateFileFromFilePath(compressRes.filePath);
                await updateRenewVisaDocumentMergedPdfFilePathApi(item.renewVisaDocumentId, { mergedPdfFilePath: compressRes.filePath });
              } else {
                const uploadFile = await uploadFileApi({ file: mergedPdf, uploadFor: FILE_UPLOAD_FOR.RENEW_VISA_FILES });
                await updateRenewVisaDocumentMergedPdfFilePathApi(item.renewVisaDocumentId, { mergedPdfFilePath: uploadFile.data.filePath });
              }
            }
          }

          if (mergedPdf && employeeFolder) {
            employeeFolder.file(`${fullName}の添付書類.pdf`, mergedPdf);
          }

          // Handle avatar file

          if (item.avatarUrl) {
            const { avatarBlob, extension } = await getUploadAvatarFile(item.avatarUrl);
            if (avatarBlob && employeeFolder) {
              employeeFolder.file(`${fullName}の顔写真.${extension}`, avatarBlob);
            }
          }
        }),
      );

      const zipBlob = await zip.generateAsync({ type: "blob" });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(zipBlob);
      link.download = renewVisaDocumentGroup.company.companyName;
      link.click();
      URL.revokeObjectURL(link.href);

      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_GROUP_DETAIL] });
      toast.success(API_SUCCESS_MESSAGE);
    } catch (error) {
      showError(error);
    }
    setIsDownloading(false);
  };

  return (
    <Button variant="outline" size="sm" onClick={handleDownload} disabled={!isCompleteStep || readonly} isLoading={isDownloading}>
      提出形式をまとめてダウンロード（ZIP形式）
    </Button>
  );
};
